import type { UserInfo } from '@mcex/types';
import type { ErrorMessageMode } from '/#/axios';
import { defineStore } from 'pinia';
import { store } from '/@/store';
import { RoleEnum } from '/@/enums/roleEnum';
import { PageEnum } from '/@/enums/pageEnum';
import {
  BROADCAST_REFRESH_PAGE_KEY,
  MENUS_KEY,
  REFRESH_TOKEN_KEY,
  ROLES_KEY,
  TOKEN_KEY,
  USER_INFO_KEY,
} from '/@/enums/cacheEnum';
import { getAuthCache, setAuthCache } from '/@/utils/auth';
import { GRANT_TYPEENUM, GetUserInfoModel, LoginParams } from '/@/api/sys/model/userModel';
import { UserApi, getUserInfo, loginApi, refreshTokenApi } from '/@/api/sys/user';
import { useI18n } from '@mcex/locale';
import { useMessage } from '/@/hooks/web/useMessage';
import { router } from '/@/router';
import { usePermissionStore } from '/@/store/modules/permission';
import { RouteRecordRaw, RouteLocationNormalizedLoaded } from 'vue-router';

import { PAGE_NOT_FOUND_ROUTE } from '/@/router/routes/basic';
import { h } from 'vue';
import Keycloak from 'keycloak-js';
import { useGlobSetting } from '/@/hooks/setting';
import { useRegisterFormStore } from './registerForm';
import { MenuItem } from '/@/api/sys/model/menuModel';
import headerImg from '/@/assets/images/header.jpg';
import { useBroadcastChannel } from '@vueuse/core';
import { DataPermissionEnum } from '@mcex/constants/src/roleEnum';
import { isDevMode } from '/@/utils/env';
import { getApiUrlByDomain } from '/@/utils/http/axios';

interface UserState {
  keycloak: Nullable<Keycloak>;
  userInfo: Nullable<UserInfo>;
  token?: string;
  refreshToken?: string;
  roleList: RoleEnum[];
  menuList: MenuItem[];
  sessionTimeout?: boolean;
  lastUpdateTime: number;
  /**
   * 失效后同时发送请求的容器 -- 缓存接口
   */
  subscribers: Function[];
  userTabelType?: string;
  usernameOrEmail: string;
  accounts: string[]; // 登录账户列表
}

export const useUserStore = defineStore({
  id: 'app-user',
  state: (): UserState => ({
    // keycloak
    keycloak: null,
    // user info
    userInfo: null,
    // token
    token: undefined,
    // refresh token
    refreshToken: undefined,
    // roleList
    roleList: [],
    // menuList
    menuList: [],
    // Whether the login expired
    sessionTimeout: false,
    // Last fetch time
    lastUpdateTime: 0,
    subscribers: [],
    userTabelType: undefined,
    usernameOrEmail: '',
    accounts: [], // 登录账户列表
  }),
  getters: {
    getUserInfo(): UserInfo {
      const userInfo = this.userInfo || getAuthCache<UserInfo>(USER_INFO_KEY);
      const avatarPath = userInfo?.avatarPath ?? '';
      const apiUrl = isDevMode()
        ? 'https://exmt-dev1-api.microconnect.cc'
        : getApiUrlByDomain(window.location.href);
      const avatar = avatarPath ? `${apiUrl}${UserApi.GetAvatar}?path=${avatarPath}` : headerImg;
      return {
        ...userInfo,
        avatar,
      };
    },
    getToken(): string {
      return this.token || getAuthCache<string>(TOKEN_KEY);
    },
    getRefreshToken(): string {
      return this.refreshToken || getAuthCache<string>(REFRESH_TOKEN_KEY);
    },
    getRoleList(): RoleEnum[] {
      return this.roleList.length > 0 ? this.roleList : getAuthCache<RoleEnum[]>(ROLES_KEY);
    },
    getMenuCodes(): string[] {
      // 深度遍历
      const deepTraversal = (node: MenuItem, menuCode: string[]) => {
        menuCode.push(node.menuCode);
        if (node.childMenu) {
          node.childMenu.forEach((item) => {
            deepTraversal(item, menuCode);
          });
        }
      };
      const menuCode: string[] = [];
      this.menuList.forEach((item) => {
        deepTraversal(item, menuCode);
      });
      return menuCode;
    },
    getSessionTimeout(): boolean {
      return !!this.sessionTimeout;
    },
    getLastUpdateTime(): number {
      return this.lastUpdateTime;
    },
    /**
     * 获取当前用户数据权限
     */
    getDataPermissionList(): DataPermissionEnum[] {
      return this.userInfo?.dataLevels ?? [];
    },
  },
  actions: {
    setUserNameOrEmail(usernameOrEmail: string) {
      this.usernameOrEmail = usernameOrEmail;
    },
    setAccounts(accounts: string[]) {
      this.accounts = accounts;
    },
    setKeyCloak(keycloak: Keycloak) {
      this.keycloak = keycloak;
    },
    setToken(info: string | undefined) {
      this.token = info ? info : ''; // for null or undefined value
      setAuthCache(TOKEN_KEY, info);
    },
    setRefreshToken(info: string | undefined) {
      this.refreshToken = info ? info : ''; // for null or undefined value
      setAuthCache(REFRESH_TOKEN_KEY, info);
    },
    setRoleList(roleList: RoleEnum[]) {
      this.roleList = roleList;
      setAuthCache(ROLES_KEY, roleList);
    },
    setMenuList(menuList: MenuItem[]) {
      this.menuList = menuList;
      setAuthCache(MENUS_KEY, menuList);
    },
    setUserInfo(info: UserInfo | null) {
      this.userInfo = info;
      this.lastUpdateTime = new Date().getTime();
      setAuthCache(USER_INFO_KEY, info);
    },
    setSessionTimeout(flag: boolean) {
      this.sessionTimeout = flag;
    },
    resetState() {
      this.userInfo = null;
      this.token = '';
      this.roleList = [];
      this.sessionTimeout = false;
    },
    /**
     * @description: login
     */
    async login(
      params: LoginParams & {
        goHome?: boolean;
        mode?: ErrorMessageMode;
        currentRoute?: RouteLocationNormalizedLoaded;
      },
    ): Promise<GetUserInfoModel | null> {
      try {
        const { goHome = true, mode, currentRoute, ...loginParams } = params;
        const res = await loginApi(loginParams, mode);
        // 检测到您的邮箱地址关联多个会员，请使用登录ID进行登录
        if (res.code === 1101004035) {
          return Promise.reject(res);
        }
        const { access_token: token, refresh_token: refreshToken } = res.data;
        // save token
        this.setToken(token);
        this.setRefreshToken(refreshToken);
        return this.afterLoginAction(goHome, currentRoute);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async afterLoginAction(
      goHome = false,
      currentRoute?: RouteLocationNormalizedLoaded,
    ): Promise<GetUserInfoModel | null> {
      if (!this.getToken) return null;
      // get user info
      const userInfo = await this.getUserInfoAction();
      const sessionTimeout = this.sessionTimeout;
      if (sessionTimeout) {
        this.setSessionTimeout(false);
        return userInfo;
      }
      const permissionStore = usePermissionStore();

      if (!permissionStore.isDynamicAddedRoute) {
        const routes = await permissionStore.buildRoutesAction(currentRoute);
        routes.forEach((route) => {
          router.addRoute(route as unknown as RouteRecordRaw);
        });
        router.addRoute(PAGE_NOT_FOUND_ROUTE as unknown as RouteRecordRaw);
        permissionStore.setLastBuildMenuTime();
        permissionStore.setDynamicAddedRoute(true);
      }
      // 广播
      const { isSupported, post, close } = useBroadcastChannel({
        name: BROADCAST_REFRESH_PAGE_KEY,
      });
      if (isSupported) {
        const currentRoute = router.currentRoute.value;
        const toPackage =
          currentRoute.matched[0]?.meta?.package ||
          ((currentRoute.query?.redirect ?? '') as string).split('/')[1];
        post({ fresh: true, currentPackage: toPackage });
        close();
      }
      // 获取url参数redirect
      const redirect = router.currentRoute.value.query.redirect as string;
      // redirect存在则跳转到redirect，否则跳转到homePath
      if (redirect) {
        await router.replace(redirect);
        return userInfo;
      }
      goHome && (await router.replace(PageEnum.BASE_HOME));
      return userInfo;
    },
    async getUserInfoAction(): Promise<UserInfo | null> {
      //if (!this.getToken) return null;
      const userInfo = await getUserInfo();
      const { roles = [], menus = [] } = userInfo;
      this.setRoleList(roles);
      this.setMenuList(menus);
      this.setUserInfo(userInfo);
      if (this.roleList.includes(RoleEnum.ORDINARY)) {
        // 异步获取申请记录
        useRegisterFormStore().fetchOnboardings();
      }
      return userInfo;
    },
    /**
     * 前往页面，如果是MT这前往Home游客
     * package状态下前往登录页
     * @param goPage 跳转页面 默认为登录页
     */
    goPage(goPage: PageEnum = PageEnum.BASE_LOGIN) {
      let redirect:
        | PageEnum
        | {
            path: PageEnum;
            query: {
              redirect: string;
            };
          } = goPage;
      const toPackage = router.currentRoute.value.matched[0]?.meta?.package;
      if (toPackage) {
        redirect = {
          path: PageEnum.BASE_LOGIN,
          query: {
            redirect: router.currentRoute.value.fullPath,
          },
        };
      }
      router.push(redirect);
    },
    /**
     * @description: logout
     */
    async logout(goLogin = false) {
      if (this.getToken) {
        try {
          const { useKc } = useGlobSetting();
          if (useKc === 'true') {
            //kc登出
            this.keycloak?.logout();
          } else {
            const permissionStore = usePermissionStore();

            this.setToken(undefined);
            this.setRefreshToken(undefined);
            this.setSessionTimeout(false);
            this.setUserInfo(null);
            permissionStore.resetState();
            // 广播
            const { isSupported, post, close } = useBroadcastChannel({
              name: BROADCAST_REFRESH_PAGE_KEY,
            });
            if (isSupported) {
              const toPackage = router.currentRoute.value.matched[0]?.meta?.package;
              post({ fresh: true, currentPackage: toPackage });
              close();
            }
            if (goLogin) {
              this.goPage();
            }
          }
        } catch {
          console.log('注销Token失败');
        }
      }
    },

    /**
     * @description: Confirm before logging out
     */
    confirmLoginOut() {
      const { createConfirm } = useMessage();
      const { t } = useI18n();
      const permissionStore = usePermissionStore();

      createConfirm({
        iconType: 'warning',
        title: () => h('span', t('sys.app.logoutMessage')),
        // content: () => h('span', t('sys.app.logoutMessage')),
        onOk: async () => {
          // 清除登录状态回到首页
          await this.logout();
          // 清除菜单
          permissionStore.setFrontMenuList([]);
          permissionStore.setLastBuildMenuTime();
          this.setMenuList([]); // 清除菜单, 切换账号登陆时，权限初始判断会拿到上次的结果
          // 跳转到首页
          this.goPage(PageEnum.HOME);
        },
      });
    },
    async refreshTokenAction() {
      try {
        const { kcClientId } = useGlobSetting();
        const queryParams = {
          refresh_token: this.getRefreshToken,
          client_id: kcClientId || '',
          grant_type: GRANT_TYPEENUM.REFRESH,
        };
        const res = await refreshTokenApi(queryParams);
        const { access_token: token, refresh_token: refreshToken } = res.data;

        // save token
        this.setToken(token);
        this.setRefreshToken(refreshToken);
        return true;
      } catch (error) {
        return Promise.reject(error);
      }
    },
    /**
     * 添加缓存接口
     * @param callback 刷新token后执行
     */
    addSubscriber(callback: Function) {
      this.subscribers.push(callback);
    },
    /**
     * 清空缓存接口
     */
    clearSubscriber() {
      this.subscribers = [];
    },
    /**
     * 刷新 token 后, 将缓存的接口重新请求一次
     * @param newToken 刷新后的token
     */
    doSubscriber() {
      this.subscribers.forEach((callback: Function) => {
        callback();
      });
      // 清空缓存接口
      this.clearSubscriber();
    },
  },
});

// Need to be used outside the setup
export function useUserStoreWithOut() {
  return useUserStore(store);
}
